import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Collapsible section accordion for ChatContainer sidebar.
 * Follows presentational component pattern.
 */
const ChatSection = ({
  title,
  icon,
  count = 0,
  unreadCount = 0,
  isOpen = true,
  onToggle,
  action = null,
  accentColor = '#3b82f6',
  isDark = false,
  children
}) => {
  if (count === 0 && unreadCount === 0) {
    return null;
  }

  return (
    <div className="w-full mb-3 select-none">
      {/* Section Header */}
      <div className="flex items-center justify-between px-2.5 py-1.5 rounded-xl hover:bg-slate-100/60 dark:hover:bg-slate-800/40 transition-colors">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 bg-transparent border-0 text-left cursor-pointer p-0 m-0 min-w-0"
          aria-expanded={isOpen}
        >
          <span className="text-slate-400 dark:text-slate-500 transition-transform duration-200 shrink-0">
            {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </span>

          <span
            className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider truncate"
            style={{ color: accentColor }}
          >
            {icon}
            <span className="truncate">{title}</span>
          </span>

          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-200/60 dark:bg-slate-700/60 text-slate-500 dark:text-slate-400 shrink-0">
            {count}
          </span>
        </button>

        <div className="flex items-center gap-2 shrink-0 ml-2">
          {/* Unread pill badge if section is collapsed and has unread messages */}
          {!isOpen && unreadCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full shadow-sm animate-pulse">
              {unreadCount}
            </span>
          )}

          {/* Action button (e.g. "+" for create group or new conversation) */}
          {action}
        </div>
      </div>

      {/* Section Content */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: 'auto',
              opacity: 1,
              transition: { height: { duration: 0.22, ease: 'easeOut' }, opacity: { duration: 0.2 } }
            }}
            exit={{
              height: 0,
              opacity: 0,
              transition: { height: { duration: 0.18, ease: 'easeIn' }, opacity: { duration: 0.15 } }
            }}
            className="overflow-hidden mt-1.5 space-y-1.5"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatSection;
